import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { FILTER_SEPARATOR } from "@/constants";
import {
	Box,
	Divider,
	Table,
	TableBody,
	TableCell,
	TablePagination,
	TableRow,
	Typography,
	useColorScheme,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { CustomSkeleton } from "../customSkeleton/custom-skeleton";
import { FieldType } from "../dynamicField/types";
import { CustomRow } from "./custom-row";
import { TableHeadCustom } from "./table-head";
import { TableToolbar } from "./table-toolbar";
import {
	Column,
	ExpandableRowConfig,
	FilterCondition,
	FilterDrawerOption,
	FilterOperator,
	FilterOption,
	Filters,
	FiltersDrawer,
	FilterType,
	Option,
} from "./types";

interface CustomTableProps {
	data: any[];
	totalItems: number;
	refetchData: (query?: string) => void;
	isLoading: boolean;
	filtersOptions?: FilterOption[];
	filtersDrawerOptions?: FilterDrawerOption[];
	columns: Column[];
	hasSelectedRows?: boolean;
	selectedRowActions?: any[];
	enableSelectAllMode?: boolean;
	expandableRow?: ExpandableRowConfig;
	withoutFetchPagination?: boolean;
	renderHeaderButtons?: () => React.ReactNode;
	isFilterDrawerDisabled?: boolean;
	// Advanced filters
	availableFields?: FieldType[];
	tableHeaderTitle?: string;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PER_PAGE = 10;

export function CustomTable({
	filtersOptions = [],
	filtersDrawerOptions = [],
	data,
	totalItems,
	refetchData = () => {},
	isLoading,
	columns,
	hasSelectedRows,
	selectedRowActions = [],
	enableSelectAllMode = false,
	expandableRow,
	withoutFetchPagination,
	renderHeaderButtons,
	isFilterDrawerDisabled = false,
	availableFields = [],
	tableHeaderTitle,
}: CustomTableProps) {
	const [searchParams, setSearchParams] = useSearchParams();

	const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(
		searchParams.get("sort_order") as "asc" | "desc" | null
	);
	const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "");
	const [rowsPerPage, setRowsPerPage] = useState(Number(searchParams.get("per_page")) || DEFAULT_PER_PAGE);
	const [page, setPage] = useState(Number(searchParams.get("page")) || 0);
	const [maxDataLength, setMaxDataLength] = useState(0);

	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [isAllDataSelected, setIsAllDataSelected] = useState(false);

	const [filters, setFilters] = useState<Filters>(() => {
		return filtersOptions.reduce((acc, filter) => {
			if (filter.type === FilterType.ATTEMPTS) {
				const from = searchParams.get(`${filter.name}From`) || "";
				const to = searchParams.get(`${filter.name}To`) || "";
				acc[filter.name] = {
					...filter,
					value: from || to ? { from: from || undefined, to: to || undefined } : undefined,
				};
				return acc;
			}

			const isArray =
				filter.type === FilterType.MULTI_SELECT ||
				filter.type === FilterType.MULTI_SELECT_FREE_SOLO ||
				filter.type === FilterType.FETCH_MULTI_SELECT;
			let value: any = isArray ? [] : "";

			if (isArray && searchParams.getAll(filter.name).length > 0) {
				value = searchParams.getAll(filter.name).map((v) => {
					const splittedValue = v.split(FILTER_SEPARATOR);
					return { label: splittedValue.slice(0, -1).join(FILTER_SEPARATOR), value: splittedValue.at(-1) };
				});
			}

			if (!isArray) {
				const splittedValue = searchParams.get(filter.name)?.split(FILTER_SEPARATOR);
				value =
					filter.type === FilterType.FETCH_SELECT || filter.type === FilterType.SELECT
						? {
								label: splittedValue?.slice(0, -1).join(FILTER_SEPARATOR),
								value: splittedValue?.at(-1),
							}
						: searchParams.get(filter.name) || "";
			}

			acc[filter.name] = {
				...filter,
				value: value,
			};

			return acc;
		}, {} as Filters);
	});

	const [drawerFilters, setDrawerFilters] = useState<FiltersDrawer>(() => {
		return filtersDrawerOptions.reduce((acc, filter) => {
			const value = searchParams.get(filter.name) || "";
			acc[filter.name] = {
				...filter,
				value: value,
			};

			return acc;
		}, {} as FiltersDrawer);
	});

	const [conditions, setConditions] = useState<FilterCondition[]>(JSON.parse(searchParams.get("filters") || "[]"));

	// useEffect(() => {
	//   if (isFilterDrawerDisabled) {
	//     return;
	//   }

	//   setDrawerFilters(filtersDrawerOptions.reduce((acc, filter) => {
	//     const value = searchParams.get(filter.name) || "";
	//     acc[filter.name] = {
	//       ...filter,
	//       value: value,
	//     };

	//     return acc;
	//   }, {} as FiltersDrawer));
	// }, [isFilterDrawerDisabled]);

	const { colorScheme = "light" } = useColorScheme();

	useEffect(() => {
		const newSortOrder = searchParams.get("sort_order") as "asc" | "desc" | null;
		const newSortBy = searchParams.get("sort_by") || "";
		const newRowsPerPage = Number(searchParams.get("per_page")) || DEFAULT_PER_PAGE;
		const newPage = Number(searchParams.get("page")) || 0;

		if (newSortOrder !== sortOrder) {
			setSortOrder(newSortOrder);
		}
		if (newSortBy !== sortBy) {
			setSortBy(newSortBy);
		}
		if (newRowsPerPage !== rowsPerPage) {
			setRowsPerPage(newRowsPerPage);
		}
		if (newPage !== page) {
			setPage(newPage);
		}

		const newFilters = filtersOptions.reduce((acc, filter) => {
			if (filter.type === FilterType.ATTEMPTS) {
				const from = searchParams.get(`${filter.name}From`) || "";
				const to = searchParams.get(`${filter.name}To`) || "";
				acc[filter.name] = {
					...filter,
					value: from || to ? { from: from || undefined, to: to || undefined } : undefined,
				};
				return acc;
			}

			const isArray =
				filter.type === FilterType.MULTI_SELECT ||
				filter.type === FilterType.MULTI_SELECT_FREE_SOLO ||
				filter.type === FilterType.FETCH_MULTI_SELECT;
			let value: any = isArray ? [] : "";

			if (isArray && searchParams.getAll(filter.name).length > 0) {
				value = searchParams.getAll(filter.name).map((v) => {
					const splittedValue = v.split(FILTER_SEPARATOR);
					return { label: splittedValue.slice(0, -1).join(FILTER_SEPARATOR), value: splittedValue.at(-1) };
				});
			}

			if (!isArray) {
				const splittedValue = searchParams.get(filter.name)?.split(FILTER_SEPARATOR);
				value =
					filter.type === FilterType.FETCH_SELECT || filter.type === FilterType.SELECT
						? {
								label: splittedValue?.slice(0, -1).join(FILTER_SEPARATOR),
								value: splittedValue?.at(-1),
							}
						: searchParams.get(filter.name) || "";
			}

			acc[filter.name] = {
				...filter,
				value: value,
			};

			return acc;
		}, {} as Filters);

		const isFiltersChanged = Object.entries(newFilters).some(([key, filter]) => {
			const newValue = filter.value;
			const oldValue = filters[key].value;

			return Array.isArray(filter.value) && typeof newValue === "object" && typeof oldValue === "object"
				? (newValue as Option[]).length !== (oldValue as Option[]).length ||
						(newValue as Option[]).some(
							(v, index) =>
								v.label !== (oldValue as Option[])[index].label || v.value !== (oldValue as Option[])[index].value
						)
				: filter.type === FilterType.FETCH_SELECT || filter.type === FilterType.SELECT
					? (newValue as Option)?.label !== (oldValue as Option)?.label ||
						(newValue as Option)?.value !== (oldValue as Option)?.value
					: newValue !== oldValue;
		});

		const newConditions = JSON.parse(searchParams.get("filters") || "[]");

		if (newConditions !== conditions) {
			setConditions(newConditions);
		}

		if (isFiltersChanged) {
			setFilters(newFilters);
		}
	}, [searchParams]);

	const totalPages = Math.ceil(totalItems / rowsPerPage);

	const fetchData = () => {
		const query: string[] = [];

		if (sortOrder) {
			query.push(`sort_order=${sortOrder}`);
		}

		if (sortBy) {
			query.push(`sort_by=${sortBy}`);
		}

		if (page >= 0) {
			query.push(`offset=${page * rowsPerPage}`);
		}

		if (rowsPerPage) {
			query.push(`limit=${rowsPerPage}`);
		}

		for (const filter of filtersOptions) {
			const value = filters[filter.name]?.value;

			if (filter.type === FilterType.ATTEMPTS) {
				const attemptsValue = value as { from?: string; to?: string };
				if (attemptsValue?.from) {
					query.push(`${filter.name}From=${attemptsValue.from}`);
				}
				if (attemptsValue?.to) {
					query.push(`${filter.name}To=${attemptsValue.to}`);
				}
			} else if (filter.type === FilterType.FETCH_SELECT || filter.type === FilterType.SELECT) {
				if ((value as Option)?.value) {
					query.push(`${filter.name}=${(value as Option).value}`);
				}
			} else if (value && Array.isArray(value)) {
				for (const v of value) {
					query.push(`${filter.name}=${(v as Option).value}`);
				}
			} else if (value) {
				query.push(`${filter.name}=${value}`);
			}
		}

		const filtersDrawerQuery: string[] = [];

		for (const filter of filtersDrawerOptions) {
			const value = drawerFilters[filter.name]?.value;

			if (value) {
				filtersDrawerQuery.push(`${filter.name}=${value}`);
			}
		}

		let filtersQuery = "";

		const formattedConditions = conditions.map((condition) => {
			const isMultiple = condition.operator === FilterOperator.IN || condition.operator === FilterOperator.NOT_IN;
			if (isMultiple && Array.isArray(condition.value)) {
				return {
					...condition,
					value: condition.value?.map((v) => v?.value) || [],
				};
			}
			return {
				...condition,
				value: condition.value?.[0]?.value || condition?.value || "",
			};
		});

		filtersQuery = conditions.length > 0 ? "&filters=" + encodeURIComponent(JSON.stringify(formattedConditions)) : "";

		refetchData([...query, ...filtersDrawerQuery].join("&") + filtersQuery);
	};

	useEffect(() => {
		fetchData();
	}, [sortOrder, sortBy, page, rowsPerPage, filters, drawerFilters, conditions]);

	React.useLayoutEffect(() => {
		if (page + 1 > totalPages && totalPages !== 0) {
			setPage(0);
			searchParams.delete("page");
			setSearchParams(searchParams);
		}
	}, [totalPages]);

	React.useLayoutEffect(() => {
		setMaxDataLength((prev) => (data.length === 0 ? prev : data.length));
	}, [data]);

	const resetFilters = () => {
		setFilters(
			filtersOptions.reduce((acc, filter) => {
				acc[filter.name] = filter;
				return acc;
			}, {} as Filters)
		);
	};

	const handleSort = (name: string) => {
		if (name === sortBy) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
			searchParams.set("sort_order", sortOrder === "asc" ? "desc" : "asc");
			searchParams.set("sort_by", name);
			if (sortOrder === "desc") {
				setSortBy("");
				setSortOrder(null);
				searchParams.delete("sort_by");
				searchParams.delete("sort_order");
			}
		} else {
			setSortBy(name);
			setSortOrder("asc");
			searchParams.set("sort_order", "asc");
			searchParams.set("sort_by", name);
		}

		setSearchParams(searchParams);
	};

	// const handleSelectAllRows = (checked: boolean) => {
	// 	const mappedData = data.map((row) => row.id);

	// 	setSelectedRows((prev) =>
	// 		checked
	// 			? [...prev.filter((id) => !mappedData.includes(id)), ...mappedData]
	// 			: prev.filter((id) => !mappedData.includes(id))
	// 	);
	// };

	// This is used to check if the table is at the end of the page
	const [atEnd, setAtEnd] = useState(true);

	const containerRef = React.useRef<HTMLDivElement | null>(null);

	const handleScroll = React.useCallback(() => {
		if (!containerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

		setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
	}, []);

	useEffect(() => {
		setTimeout(() => {
			handleScroll();
		}, 100);
	}, [data, columns, handleScroll]);

	useEffect(() => {
		const handleResize = () => {
			setTimeout(() => {
				handleScroll();
			}, 100);
		};

		globalThis.addEventListener("resize", handleResize);

		return () => {
			globalThis.removeEventListener("resize", handleResize);
		};
	}, [handleScroll]);

	const headCells = columns.filter((column) => !column.hideColumn).map((column) => column.header);
	const columnsToRender = columns.filter((column) => !column.hideColumn);

	const handleRowExpand = (rowId: string) => {
		setExpandedRows((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
	};

	const visibleData = withoutFetchPagination ? data.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : data;
	const updatedFilters = filtersOptions.reduce((acc, filter) => {
		acc[filter.name] = {
			...filters[filter.name],
			getOptionsUrl: filter.getOptionsUrl,
		};
		return acc;
	}, {} as Filters);

	const allActionsColumnsAreHidden = useMemo(
		() =>
			columnsToRender?.at(-1)?.accessorKey === "actions" &&
			visibleData?.every((row) => columnsToRender?.at(-1)?.getCellValue?.(row) === null),
		[columns, data]
	);

	return (
		<Box>
			{(filtersOptions.length > 0 ||
				renderHeaderButtons ||
				selectedRowActions?.length > 0 ||
				filtersDrawerOptions.length > 0 ||
				availableFields.length > 0 ||
				tableHeaderTitle) && (
				<>
					<TableToolbar
						tableHeaderTitle={tableHeaderTitle}
						filters={updatedFilters}
						setFilters={setFilters}
						resetFilters={resetFilters}
						renderHeaderButtons={renderHeaderButtons}
						selectedRowActions={selectedRowActions}
						selectedRows={selectedRows}
						isAllDataSelected={isAllDataSelected}
						totalItems={totalItems}
						drawerFilters={drawerFilters}
						setDrawerFilters={setDrawerFilters}
						filtersDrawerOptions={filtersDrawerOptions}
						isFilterDrawerDisabled={isFilterDrawerDisabled}
						availableFields={availableFields}
						conditions={conditions}
						setConditions={setConditions}
						resetAllDataSelected={() => {
							setIsAllDataSelected(false);
							setSelectedRows([]);
						}}
					/>
					<Divider />
				</>
			)}

			<Box sx={{ overflowX: "auto" }} onScroll={handleScroll} ref={containerRef}>
				<Table size="medium">
					<TableHeadCustom
						order={sortOrder as "asc" | "desc"}
						orderBy={sortBy}
						headCells={headCells}
						rowCount={visibleData?.length}
						onSort={handleSort}
						hideSelectAll={hasSelectedRows ? false : true}
						onSelectAllRows={(checked) => {
							setSelectedRows((prev) =>
								checked
									? [...prev, ...visibleData.filter((row) => !prev.includes(row.id)).map((row) => row.id)]
									: prev.filter((id) => !visibleData.some((row) => row.id === id))
							);
						}}
						onDeselectAllRows={() => {
							setSelectedRows([]);
						}}
						hasExpandableRows={expandableRow?.enabled || false}
						enableSelectAllMode={enableSelectAllMode}
						isAllDataSelected={isAllDataSelected}
						setIsAllDataSelected={setIsAllDataSelected}
						totalItems={totalItems}
						selectedRowCount={selectedRows.filter((id) => visibleData.some((row) => row.id === id)).length}
						selectedRows={selectedRows}
					/>

					<TableBody>
						{isLoading ? (
							Array.from({ length: maxDataLength || rowsPerPage }).map((_, index): React.JSX.Element => {
								return (
									<TableRow key={index}>
										{expandableRow?.enabled && (
											<TableCell padding="checkbox">
												<CustomSkeleton variant="rounded" width="20" height={20} />
											</TableCell>
										)}
										{hasSelectedRows && (
											<TableCell padding="checkbox">
												<CustomSkeleton variant="rounded" width="20" height={20} />
											</TableCell>
										)}
										{columnsToRender.map((column, index) => (
											<TableCell key={index}>
												<CustomSkeleton variant="rounded" height={22} width={column.header.width} />
											</TableCell>
										))}
									</TableRow>
								);
							})
						) : totalItems === 0 ? (
							<TableRow>
								<TableCell
									colSpan={columnsToRender.length + (hasSelectedRows ? 1 : 0) + (expandableRow?.enabled ? 1 : 0)}
								>
									<Typography color="text.secondary" sx={{ textAlign: "center" }} variant="body2">
										No data found
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							visibleData?.map((row) => (
								<React.Fragment key={row.id}>
									<CustomRow
										row={row}
										columns={columnsToRender}
										isSelected={selectedRows.includes(row.id)}
										setIsSelected={(isSelected) =>
											setSelectedRows(
												isSelected ? [...selectedRows, row.id] : selectedRows.filter((id) => id !== row.id)
											)
										}
										hideSelect={hasSelectedRows ? false : true}
										expandableRow={expandableRow}
										isExpanded={expandedRows.includes(row.id)}
										onExpand={() => handleRowExpand(row.id)}
										isAtEnd={atEnd}
										isAllDataSelected={isAllDataSelected}
										allActionsColumnsAreHidden={allActionsColumnsAreHidden}
									/>
									{expandableRow?.enabled && expandedRows.includes(row.id) && (
										<TableRow>
											<TableCell
												colSpan={columnsToRender.length + (hasSelectedRows ? 1 : 0) + (expandableRow?.enabled ? 1 : 0)}
												sx={{
													padding: 0,
													borderBottom: "none",
													backgroundColor: colorScheme === "light" ? "grey.100" : "#191e20",
												}}
											>
												{expandableRow.renderExpandedContent(row)}
											</TableCell>
										</TableRow>
									)}
								</React.Fragment>
							))
						)}
					</TableBody>
				</Table>
			</Box>

			<Divider />
			<TablePagination
				component="div"
				count={totalItems}
				onPageChange={(event, newPage) => {
					setPage(newPage);
					if (Number(newPage) === 0) {
						searchParams.delete("page");
					} else {
						searchParams.set("page", newPage.toString());
					}
					setSearchParams(searchParams);
				}}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(Number(event.target.value));
					setPage(0);
					searchParams.delete("page");

					if (Number(event.target.value) === DEFAULT_PER_PAGE) {
						searchParams.delete("per_page");
					} else {
						searchParams.set("per_page", event.target.value.toString());
					}
					setSearchParams(searchParams);
				}}
				page={page}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
				slotProps={{
					menuItem: {
						style: {
							fontSize: "16px",
						},
					},
				}}
			/>
		</Box>
	);
}
